import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { adminApi } from "@/lib/adminApi";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft, Check, X, Eye, EyeOff, Shield, ShieldOff, Save, FileText, Image as ImageIcon,
} from "lucide-react";

const NotCollected = () => (
  <span className="text-[11px] uppercase tracking-wider text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">Not collected yet</span>
);

const CheckPill = ({ state, label }) => {
  // state = true/false/null
  if (state === true) return <Badge className="bg-emerald-100 text-emerald-800 inline-flex items-center gap-1"><Shield className="w-3 h-3"/> {label} · verified</Badge>;
  if (state === false) return <Badge className="bg-rose-100 text-rose-800 inline-flex items-center gap-1"><ShieldOff className="w-3 h-3"/> {label} · failed</Badge>;
  return <Badge className="bg-slate-100 text-slate-600 inline-flex items-center gap-1">{label} · MVP not integrated</Badge>;
};

const REJECT_REASONS = [
  "Document unclear or blurry",
  "Name mismatch across documents",
  "Suspected forged document",
  "Selfie does not match ID photo",
  "Vehicle document expired",
  "Other",
];

const KycDetail = () => {
  const { uid } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [signedUrl, setSignedUrl] = useState("");
  const [revealed, setRevealed] = useState({ aadhaar: "", pan: "" });
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectNote, setRejectNote] = useState("");
  const [approveOpen, setApproveOpen] = useState(false);
  const [approveNote, setApproveNote] = useState("");
  const [internalNote, setInternalNote] = useState("");
  const [busy, setBusy] = useState(false);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  const load = () =>
    adminApi.get(`/users/${uid}/kyc`)
      .then((r) => {
        setData(r.data);
        setInternalNote(r.data.kyc.internal_admin_note || "");
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load"));

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [uid]);

  const openDocument = async () => {
    try {
      const r = await adminApi.get(`/users/${uid}/kyc-file`);
      setSignedUrl(`${BACKEND}${r.data.url}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't fetch KYC file");
    }
  };

  const revealField = async (field) => {
    if (revealed[field]) {
      setRevealed({ ...revealed, [field]: "" });
      return;
    }
    try {
      const r = await adminApi.post(`/users/${uid}/kyc/reveal`, { user_id: uid, field });
      if (!r.data.collected) {
        toast("Not collected yet", { description: `No ${field.toUpperCase()} number has been captured for this user.` });
        return;
      }
      setRevealed({ ...revealed, [field]: r.data.value });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reveal failed");
    }
  };

  const decide = async (approve) => {
    setBusy(true);
    try {
      await adminApi.post(`/users/${uid}/kyc/decide`, {
        approve,
        reason: approve ? "" : rejectReason,
        note: approve ? approveNote : rejectNote,
      });
      toast.success(approve ? "Approved" : "Rejected");
      nav("/admin/kyc");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
    setBusy(false);
  };

  const saveNote = async () => {
    try {
      await adminApi.post(`/users/${uid}/kyc/note`, { note: internalNote });
      toast.success("Internal note saved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save note");
    }
  };

  if (!data) return <div className="p-8 text-slate-400">Loading…</div>;

  const p = data.profile;
  const k = data.kyc;
  const contentType = k.doc_meta?.content_type || "";
  const isImage = contentType.startsWith("image/");
  const isPdf = contentType === "application/pdf";

  return (
    <div className="p-8 max-w-5xl mx-auto" data-testid="admin-kyc-detail">
      <Link to="/admin/kyc" data-testid="back-to-queue" className="inline-flex items-center gap-1 text-sm text-slate-500 mb-4 hover:text-slate-900">
        <ArrowLeft className="w-4 h-4"/> Back to queue
      </Link>

      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-2xl font-extrabold text-slate-900">{p.name || "Unnamed carrier"}</h1>
          <div className="text-sm text-slate-500 mt-0.5">{p.phone} {p.email ? `· ${p.email}` : ""}</div>
          <div className="mt-2 flex items-center gap-2">
            <Badge className={
              k.doc_id ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-600"
            }>KYC · {p.kyc_status || "pending"}</Badge>
            <Badge className="bg-slate-100 text-slate-700 uppercase tracking-wider">Trust · {p.trust_tier || "new"}</Badge>
          </div>
        </div>
        {p.kyc_status === "pending" && k.doc_id && (
          <div className="flex items-center gap-2">
            <Button data-testid="btn-approve" onClick={() => setApproveOpen(true)} className="bg-emerald-600 hover:bg-emerald-700 rounded-full">
              <Check className="w-4 h-4 mr-1"/> Approve
            </Button>
            <Button data-testid="btn-reject" onClick={() => setRejectOpen(true)} variant="outline" className="border-rose-300 text-rose-700 rounded-full">
              <X className="w-4 h-4 mr-1"/> Reject
            </Button>
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Personal details */}
        <section className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="section-personal">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-3 font-semibold">Personal details</div>
          <dl className="text-sm space-y-2">
            <Row label="Name" value={p.name}/>
            <Row label="Phone" value={p.phone}/>
            <Row label="Email" value={p.email || null}/>
            <Row label="City" value={p.city || null}/>
            <Row label="Date of birth" value={p.dob || null}/>
            <Row label="Emergency contact" value={p.emergency_contact || null}/>
            <Row label="Signed up" value={p.created_at?.slice(0, 10)}/>
          </dl>
        </section>

        {/* Selfie vs Aadhaar photo */}
        <section className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="section-photo">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-3 font-semibold">Selfie vs ID photo</div>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl border border-dashed border-slate-300 aspect-square grid place-items-center bg-slate-50 text-center text-xs text-slate-400 p-3">
              <div>
                <ImageIcon className="w-6 h-6 text-slate-300 mx-auto mb-1"/>
                Selfie
                <div className="mt-1"><NotCollected/></div>
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 aspect-square bg-slate-50 overflow-hidden grid place-items-center">
              {signedUrl ? (
                isImage ? <img src={signedUrl} alt="KYC" className="w-full h-full object-contain" data-testid="kyc-image"/>
                : isPdf ? <a href={signedUrl} target="_blank" rel="noreferrer" className="text-amber-700 underline text-sm inline-flex items-center gap-1"><FileText className="w-4 h-4"/> Open PDF</a>
                : <a href={signedUrl} target="_blank" rel="noreferrer" className="text-amber-700 underline text-sm">Open document</a>
              ) : k.doc_id ? (
                <Button data-testid="btn-open-doc" variant="outline" size="sm" onClick={openDocument}>
                  <Eye className="w-3.5 h-3.5 mr-1"/> Load document
                </Button>
              ) : (
                <div className="text-xs text-slate-400 p-3">No document uploaded.</div>
              )}
            </div>
          </div>
          {signedUrl && (
            <div className="text-[11px] text-slate-400 mt-2">Short-lived signed URL · expires in 5 minutes · view logged.</div>
          )}
        </section>

        {/* KYC verifications */}
        <section className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="section-verifications">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-3 font-semibold">Document checks</div>
          <div className="flex flex-wrap gap-2 mb-4">
            <CheckPill state={k.aadhaar_verified} label="Aadhaar"/>
            <CheckPill state={k.pan_verified} label="PAN"/>
            <CheckPill state={k.driving_licence_verified} label="Driving licence"/>
            <CheckPill state={k.vehicle_rc_verified} label="Vehicle RC"/>
            <CheckPill state={k.bank_upi_verified} label={k.upi_id ? `UPI · ${k.upi_id}` : "UPI / bank"}/>
          </div>
          <div className="text-xs text-slate-500 mb-2">Bank / UPI name match: {
            k.bank_name_match === true ? <span className="text-emerald-700">Matches profile name</span>
            : k.bank_name_match === false ? <span className="text-rose-700">Mismatch</span>
            : <NotCollected/>
          }</div>

          <div className="pt-3 border-t border-slate-100 space-y-2">
            <RevealRow
              label="Aadhaar number"
              masked={k.aadhaar_masked}
              revealed={revealed.aadhaar}
              onToggle={() => revealField("aadhaar")}
              testid="reveal-aadhaar"
            />
            <RevealRow
              label="PAN number"
              masked={k.pan_masked}
              revealed={revealed.pan}
              onToggle={() => revealField("pan")}
              testid="reveal-pan"
            />
          </div>
        </section>

        {/* Vehicles */}
        <section className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="section-vehicles">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-3 font-semibold">Vehicles</div>
          {(data.vehicles || []).length === 0 ? (
            <div className="text-sm text-slate-400">No vehicles on this profile.</div>
          ) : (
            <div className="space-y-2">
              {data.vehicles.map((v) => (
                <div key={v.type} className="rounded-xl border border-slate-200 p-3 text-sm">
                  <div className="font-medium capitalize">{v.type.replace("_", " ")}</div>
                  {["bike", "car", "suv"].includes(v.type) ? (
                    <div className="text-xs text-slate-500 mt-0.5">{v.make_model} · <span className="font-mono">{v.reg_no}</span> · {v.insurance_current ? "Insurance declared current" : "Insurance not declared current"}</div>
                  ) : (
                    <div className="text-xs text-slate-500 mt-0.5">Pre-booked transit — no vehicle document required</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Legal acceptances */}
        <section className="rounded-2xl border border-slate-200 bg-white p-5 lg:col-span-2" data-testid="section-legal">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-3 font-semibold">Legal document acceptances</div>
          {Object.keys(data.legal_acceptances).length === 0 ? (
            <div className="text-sm text-slate-400">No documents accepted yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[11px] uppercase tracking-wider text-slate-400 text-left">
                  <th className="pb-2 font-medium">Document</th>
                  <th className="pb-2 font-medium">Version</th>
                  <th className="pb-2 font-medium">Accepted at</th>
                  <th className="pb-2 font-medium">IP</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(data.legal_acceptances).map(([doc, meta]) => (
                  <tr key={doc} className="border-t border-slate-100">
                    <td className="py-2 capitalize">{doc.replace("_", " ")}</td>
                    <td className="py-2 font-mono">v{meta.version}</td>
                    <td className="py-2 font-mono text-[11px]">{meta.accepted_at?.replace("T", " ").slice(0, 19)}</td>
                    <td className="py-2 font-mono text-[11px] text-slate-400">{meta.ip || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        {/* Internal admin note */}
        <section className="rounded-2xl border border-slate-200 bg-white p-5 lg:col-span-2" data-testid="section-internal-note">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-3 font-semibold">Internal admin note</div>
          <p className="text-[11px] text-slate-400 mb-2">Only visible to admins — never shown to the carrier.</p>
          <Textarea
            data-testid="internal-note"
            value={internalNote}
            onChange={(e) => setInternalNote(e.target.value)}
            className="rounded-xl min-h-24"
            placeholder="Anything future admins should know before reviewing this carrier…"
          />
          <div className="mt-2">
            <Button data-testid="save-note-btn" size="sm" variant="outline" onClick={saveNote} className="rounded-full">
              <Save className="w-3.5 h-3.5 mr-1"/> Save note
            </Button>
          </div>
        </section>
      </div>

      {/* Reject dialog */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle>Reject KYC</DialogTitle>
            <DialogDescription>Pick a reason and add a note — both are shown to the carrier in the app.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-500">Reason</label>
              <Select value={rejectReason} onValueChange={setRejectReason}>
                <SelectTrigger data-testid="reject-reason-select" className="rounded-xl mt-1"><SelectValue placeholder="Pick a reason"/></SelectTrigger>
                <SelectContent>
                  {REJECT_REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-500">Note to carrier</label>
              <Textarea data-testid="reject-note" value={rejectNote} onChange={(e) => setRejectNote(e.target.value)} className="rounded-xl mt-1"/>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)}>Cancel</Button>
            <Button
              data-testid="confirm-reject-btn"
              disabled={busy || !rejectReason}
              onClick={() => { setRejectOpen(false); decide(false); }}
              className="bg-rose-600 hover:bg-rose-700"
            >
              Reject KYC
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve dialog */}
      <Dialog open={approveOpen} onOpenChange={setApproveOpen}>
        <DialogContent data-testid="approve-dialog">
          <DialogHeader>
            <DialogTitle>Approve KYC</DialogTitle>
            <DialogDescription>The carrier will be marked verified. This action is logged.</DialogDescription>
          </DialogHeader>
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500">Optional internal note</label>
            <Input data-testid="approve-note" value={approveNote} onChange={(e) => setApproveNote(e.target.value)} className="rounded-xl mt-1"/>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveOpen(false)}>Cancel</Button>
            <Button
              data-testid="confirm-approve-btn"
              disabled={busy}
              onClick={() => { setApproveOpen(false); decide(true); }}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Row = ({ label, value }) => (
  <div className="flex items-center gap-4">
    <dt className="text-slate-500 w-40 text-xs uppercase tracking-wider">{label}</dt>
    <dd className="text-slate-900 flex-1">{value ? value : <NotCollected/>}</dd>
  </div>
);

const RevealRow = ({ label, masked, revealed, onToggle, testid }) => (
  <div className="flex items-center gap-3">
    <div className="text-xs uppercase tracking-wider text-slate-500 w-32">{label}</div>
    <div className="font-mono text-sm text-slate-900 flex-1">{revealed || masked || <NotCollected/>}</div>
    {(masked || revealed) && (
      <button
        data-testid={testid}
        onClick={onToggle}
        className="inline-flex items-center gap-1 text-xs text-amber-700 hover:underline"
      >
        {revealed ? <><EyeOff className="w-3 h-3"/> Hide</> : <><Eye className="w-3 h-3"/> Reveal</>}
      </button>
    )}
  </div>
);

export default KycDetail;
